// TEMPORARY: dashboard-wide testing mode. One dummy live game for every vertical at once
// (concessions, retail, gates, restrooms), on the Locations page cards, the game day pages and
// the Overviews. Flip TEST_MODE to turn it on/off. It only ever runs under `npm start`
// (development); production builds can never enable it. Each vertical's own dummyLiveData.js
// flag still turns that vertical on by itself.
//
// The live game is today: gates opened 2 hours before the page loaded and the game started
// 1 hour before. Sensors report every 30 seconds and data runs up to "now", so each 60s refresh
// adds new readings. Three earlier games give the Overviews something to compare.
//
// Values are random (deterministic per reading, so refreshes never rewrite history) plus a
// rotating set of edge cases (no stale-sensor case for now; the user turned it off 2026-10-20): every sensor steps through them in 12-minute windows, offset per
// sensor, so at any moment different sensors show different cases and every case comes round.
import moment from 'moment-timezone';

const TEST_MODE = false;

export const IS_DEV = process.env.NODE_ENV === 'development';
export const USE_TEST_MODE = IS_DEV && TEST_MODE;

// Pinned when the page first loads; a full reload starts a fresh game
export const LOAD_TIME = Date.now();

// Sensors report this often
export const READING_SECONDS = 30;
// Each edge case lasts this long for a sensor before it moves to the next
export const EDGE_WINDOW_MINUTES = 12;

// Deterministic noise in [0, 1) so refreshes don't make past readings jump around
export const noise = (seed) => {
  const x = Math.sin(seed * 12.9898) * 43758.5453;
  return x - Math.floor(x);
};

// The venue's local clock (same trick the controllers use)
export const venueMoment = (timeZone, ms = Date.now()) =>
  moment(new Date(new Date(ms).toLocaleString('en-US', { timeZone: timeZone || 'America/New_York' })));

// The game day a venue-local moment belongs to (before 3 AM counts as the previous day)
const gameDayOf = (m) => (m.hour() < 3 ? m.clone().subtract(1, 'day') : m.clone()).format('YYYY-MM-DD');

// "HH:mm" -> minute of the game day (before 3 AM counts as after midnight)
export const dayMinute = (hhmm) => {
  const [h, m] = hhmm.split(':').map(Number);
  return (h < 3 ? h + 24 : h) * 60 + m;
};

// Past games for the Overviews: different times and crowd sizes
const PAST = [
  { daysAgo: 1, gameStartTime: '19:10', gatesOpen: '17:40', pregameStart: '16:00', scale: 0.85 },
  { daysAgo: 2, gameStartTime: '13:05', gatesOpen: '11:35', pregameStart: '10:00', scale: 1.15 },
  { daysAgo: 3, gameStartTime: '18:40', gatesOpen: '17:10', pregameStart: '15:30', scale: 1.0 },
];

// Today's live game, in venue time, pinned to the page load
const liveGame = (timeZone) => {
  const load = venueMoment(timeZone, LOAD_TIME);
  const gates = load.clone().subtract(2, 'hours');
  const start = load.clone().subtract(1, 'hours');
  return {
    date: gameDayOf(gates),
    gatesOpen: gates.format('HH:mm'),
    gameStartTime: start.format('HH:mm'),
    pregameStart: load.clone().subtract(4, 'hours').format('HH:mm'),
    scale: 1,
  };
};

const allGames = (timeZone) => {
  const live = liveGame(timeZone);
  const day = (daysAgo) => moment(live.date).subtract(daysAgo, 'days').format('YYYY-MM-DD');
  return [...PAST.map((g) => ({ ...g, date: day(g.daysAgo) })), live];
};

// The schedule every vertical's controller receives ('get-location-current-schedule')
export const testSchedule = (timeZone) => {
  const games = allGames(timeZone);
  const strip = ({ date, gameStartTime, gatesOpen, pregameStart }) => ({ date, gameStartTime, gatesOpen, pregameStart });
  return [
    { teamID: 2, gamesInSeries: 3, teamName: 'Test', dates: games.slice(0, 3).map(strip) },
    { teamID: 1, gamesInSeries: 1, teamName: 'Test', dates: [strip(games[3])] },
  ];
};

// One game day, everything a generator needs. Minutes and seconds count from midnight of `dateStr`
// (times after midnight keep counting past 1440). Null when there's no game that day.
export const gameContext = (timeZone, dateStr) => {
  const game = allGames(timeZone).find((g) => g.date === dateStr);
  if (!game) return null;
  const live = dateStr === liveGame(timeZone).date;
  const gates = dayMinute(game.gatesOpen);
  const start = dayMinute(game.gameStartTime);
  const nowSec = venueMoment(timeZone).diff(moment(dateStr).startOf('day'), 'seconds');
  // Live: up to now. Past games: until about 3 hours 45 minutes after the start.
  const endSec = live ? Math.min(nowSec, (27 * 60 - 1) * 60) : (start + 225) * 60;
  return {
    date: dateStr,
    live,
    gates,
    start,
    endSec,
    endMinute: Math.floor(endSec / 60),
    scale: game.scale,
    seed: Number(dateStr.replace(/-/g, '')) % 997,
  };
};

// Every reading time (seconds of the day) from `fromMinute` up to the context's end
export const readingTimes = (ctx, fromMinute) => {
  const out = [];
  for (let s = Math.max(0, fromMinute * 60); s <= ctx.endSec; s += READING_SECONDS) out.push(s);
  return out;
};

// Venue-local timestamp for a second of the game day: "YYYY-MM-DDTHH:mm:ss" (or without seconds)
export const localStamp = (dateStr, sec, withSeconds = true) =>
  moment(dateStr).startOf('day').add(sec, 'seconds').format(withSeconds ? 'YYYY-MM-DDTHH:mm:ss' : 'YYYY-MM-DDTHH:mm');

// The real instant (UTC ISO string) of a venue-local second of the game day, for routes that
// return absolute times (concessions)
export const instantISO = (timeZone, dateStr, sec) =>
  moment.tz(localStamp(dateStr, sec), 'YYYY-MM-DDTHH:mm:ss', timeZone || 'America/New_York').toISOString();

// Which edge case a sensor is in at a minute. `cases` is the vertical's list; `index` offsets each
// sensor so neighbours are in different cases.
export const edgeCase = (cases, index, minute) =>
  cases[(index + Math.floor(minute / EDGE_WINDOW_MINUTES)) % cases.length];

// Minutes into the current edge-case window (0 to EDGE_WINDOW_MINUTES - 1)
export const edgeProgress = (minute) => ((minute % EDGE_WINDOW_MINUTES) + EDGE_WINDOW_MINUTES) % EDGE_WINDOW_MINUTES;

// A smooth crowd curve shared by every vertical: arrivals build after gates open, peak just
// before the start, then waves through the game with a big break midway. 0..~1.
export const crowdCurve = (ctx, minute) => {
  const t = minute - ctx.start;
  if (minute < ctx.gates) return 0.08;
  if (t < 0) return 0.25 + 0.75 * Math.exp(-Math.pow((t + 25) / 30, 2));
  const wave = 0.35 * Math.exp(-Math.pow(((t % 40) - 28) / 7, 2));
  const midBreak = 0.7 * Math.exp(-Math.pow((t - 95) / 12, 2));
  const late = t > 190 ? Math.max(0.1, 1 - (t - 190) / 60) : 1;
  return (0.3 + wave + midBreak) * late;
};
